/**
 * Quiz Frontend Handler
 * 
 * @since 3.9.0
 */

(function($) {
    'use strict';

    // Custom Modal Function
    function showCustomModal(title, message, duration = 3000, type = 'success') {
        const modalOverlay = document.createElement('div');
        modalOverlay.className = 'custom-modal-overlay';

        const modalContent = document.createElement('div');
        modalContent.className = `custom-modal ${type}`;

        const modalHeader = document.createElement('div');
        modalHeader.className = 'custom-modal-header';

        const modalTitle = document.createElement('h3');
        modalTitle.textContent = title;
        modalHeader.appendChild(modalTitle);

        const modalBody = document.createElement('div');
        modalBody.className = 'custom-modal-body';
        modalBody.innerHTML = message;

        const progressBar = document.createElement('div');
        progressBar.className = 'custom-modal-progress';

        const progressIndicator = document.createElement('div');
        progressIndicator.className = 'custom-modal-progress-indicator';
        progressBar.appendChild(progressIndicator);

        modalContent.appendChild(modalHeader);
        modalContent.appendChild(modalBody);
        modalContent.appendChild(progressBar);
        modalOverlay.appendChild(modalContent);

        document.body.appendChild(modalOverlay);

        setTimeout(() => {
            modalOverlay.classList.add('visible');
            progressIndicator.style.transition = `width ${duration}ms linear`;
            progressIndicator.style.width = '100%';
        }, 10);

        setTimeout(() => {
            modalOverlay.classList.remove('visible');
            setTimeout(() => {
                if (document.body.contains(modalOverlay)) {
                    document.body.removeChild(modalOverlay);
                }
            }, 300);
        }, duration);

        modalOverlay.addEventListener('click', (e) => {
            if (e.target === modalOverlay) {
                modalOverlay.classList.remove('visible');
                setTimeout(() => {
                    if (document.body.contains(modalOverlay)) {
                        document.body.removeChild(modalOverlay);
                    }
                }, 300);
            }
        });
    }

    class QuizFrontend {
        constructor(container) {
            this.container = $(container);
            this.quizId = this.container.data('quiz-id');
            this.currentStep = 0;
            this.totalSteps = 0;
            this.answers = {};
            this.quiz = null;
            this.userData = {};
            this.userFormData = {}; // Store user form data across steps
            
            this.init();
        }

        init() {
            this.loadQuiz();
        }

        loadQuiz() {
            const self = this;
            
            $.ajax({
                url: simpleform_ajax.ajax_url,
                type: 'POST',
                data: {
                    action: 'simpleform_get_quiz_for_frontend',
                    quiz_id: self.quizId,
                    nonce: simpleform_ajax.nonce
                },
                success: function(response) {
                    if (response.success && response.data.quiz) {
                        self.quiz = response.data.quiz;
                        self.hasTaken = response.data.has_taken || false;
                        
                        // Check if user has already taken the quiz and retakes are not allowed
                        if (self.hasTaken && (!self.quiz.settings.allow_retake)) {
                            self.showAlreadyTaken();
                            return;
                        }
                        
                        // Check browser storage for quiz tracking
                        if (self.checkBrowserTracking()) {
                            return;
                        }
                        
                        self.renderQuiz();
                    } else {
                        self.showError(response.data.message || 'Failed to load quiz');
                    }
                },
                error: function() {
                    self.showError('Failed to load quiz. Please try again.');
                }
            });
        }

        checkBrowserTracking() {
            // Check localStorage
            const storageKey = `simpleform_quiz_taken_${this.quizId}`;
            const hasTakenFromStorage = localStorage.getItem(storageKey);
            
            if (hasTakenFromStorage && (!this.quiz.settings.allow_retake)) {
                this.showAlreadyTaken();
                return true;
            }
            
            return false;
        }

        showAlreadyTaken() {
            const html = `
                <div class="quiz-wrapper">
                    <div class="quiz-header">
                        <h2 class="quiz-title">${this.escapeHtml(this.quiz.quiz_name)}</h2>
                    </div>
                    <div class="quiz-already-taken">
                        <div class="already-taken-content">
                            <h3>📝 Quiz Already Completed</h3>
                            <p>You have already taken this quiz. ${this.quiz.settings.allow_retake ? 'You can take it again if you wish.' : 'Multiple attempts are not allowed.'}</p>
                            ${this.quiz.settings.allow_retake ? '<button class="quiz-retake-btn" type="button">Take Quiz Again</button>' : ''}
                        </div>
                    </div>
                </div>
            `;
            
            this.container.html(html);
            
            // Attach retake event if allowed
            if (this.quiz.settings.allow_retake) {
                const self = this;
                this.container.find('.quiz-retake-btn').on('click', function() {
                    self.renderQuiz();
                });
            }
        }

        renderQuiz() {
            // Check if login is required
            if (this.quiz.settings.require_login && !simpleform_ajax.is_user_logged_in) {
                this.showLoginRequired();
                return;
            }
            
            const displayMode = this.quiz.settings.display_mode || 'all_at_once';
            
            if (displayMode === 'step_by_step') {
                this.renderStepByStepQuiz();
            } else {
                this.renderAllAtOnceQuiz();
            }
        }

        showLoginRequired() {
            const html = `
                <div class="quiz-wrapper">
                    <div class="quiz-header">
                        <h2 class="quiz-title">${this.escapeHtml(this.quiz.quiz_name)}</h2>
                        ${this.quiz.description ? `<p class="quiz-description">${this.escapeHtml(this.quiz.description)}</p>` : ''}
                    </div>
                    <div class="quiz-login-required">
                        <div class="login-required-content">
                            <h3>🔒 Login Required</h3>
                            <p>You must be logged in to take this quiz.</p>
                            <a href="${simpleform_ajax.login_url || '/wp-login.php'}" class="quiz-login-btn">Login</a>
                        </div>
                    </div>
                </div>
            `;
            
            this.container.html(html);
        }

        renderAllAtOnceQuiz() {
            const needsUserInfo = this.quiz.settings.collect_name || this.quiz.settings.collect_email;
            
            const html = `
                <div class="quiz-wrapper">
                    <div class="quiz-header">
                        <h2 class="quiz-title">${this.escapeHtml(this.quiz.quiz_name)}</h2>
                        ${this.quiz.description ? `<p class="quiz-description">${this.escapeHtml(this.quiz.description)}</p>` : ''}
                    </div>
                    
                    ${needsUserInfo ? this.renderUserInfoFields() : ''}
                    
                    <div class="quiz-content">
                        ${this.renderQuestions()}
                    </div>
                    <div class="quiz-footer">
                        <button class="quiz-submit-btn" type="button">Submit Quiz</button>
                    </div>
                    <div class="quiz-results" style="display: none;"></div>
                </div>
            `;
            
            this.container.html(html);
            this.attachEvents();
        }

        renderStepByStepQuiz() {
            const questionsPerStep = this.quiz.settings.questions_per_step || 1;
            this.totalSteps = Math.ceil(this.quiz.questions.length / questionsPerStep);
            
            // Initialize current step if not set
            if (this.currentStep >= this.totalSteps) {
                this.currentStep = 0;
            }
            
            const startIndex = this.currentStep * questionsPerStep;
            const endIndex = Math.min(startIndex + questionsPerStep, this.quiz.questions.length);
            const currentQuestions = this.quiz.questions.slice(startIndex, endIndex);
            
            const needsUserInfo = this.quiz.settings.collect_name || this.quiz.settings.collect_email;
            
            const html = `
                <div class="quiz-wrapper step-by-step">
                    <div class="quiz-header">
                        <h2 class="quiz-title">${this.escapeHtml(this.quiz.quiz_name)}</h2>
                        ${this.quiz.description ? `<p class="quiz-description">${this.escapeHtml(this.quiz.description)}</p>` : ''}
                    </div>
                    
                    ${this.renderStepNavigation()}
                    
                    ${needsUserInfo && this.currentStep === 0 ? this.renderUserInfoFields() : ''}
                    
                    <div class="quiz-content">
                        <div class="quiz-step-content">
                            <div class="step-header">
                                <h3>${this.getStepTitle()}</h3>
                                <p class="step-description">
                                    ${questionsPerStep === 1 ? 
                                        `Question ${startIndex + 1} of ${this.quiz.questions.length}` :
                                        `Questions ${startIndex + 1}-${endIndex} of ${this.quiz.questions.length}`
                                    }
                                </p>
                            </div>
                            <div class="quiz-questions">
                                ${this.renderCurrentStepQuestions(currentQuestions, startIndex)}
                            </div>
                        </div>
                    </div>
                    
                    <div class="quiz-footer step-footer">
                        <div class="step-buttons">
                            ${this.currentStep > 0 ? '<button class="quiz-prev-btn" type="button">Previous</button>' : ''}
                            ${this.currentStep < this.totalSteps - 1 ? 
                                '<button class="quiz-next-btn" type="button">Next</button>' : 
                                '<button class="quiz-submit-btn" type="button">Submit Quiz</button>'
                            }
                        </div>
                    </div>
                    
                    <div class="quiz-results" style="display: none;"></div>
                </div>
            `;
            
            this.container.html(html);
            this.attachStepEvents();
            
            // Restore user form data after rendering
            this.restoreUserFormData();
        }

        renderUserInfoFields() {
            const collectName = this.quiz.settings.collect_name;
            const collectEmail = this.quiz.settings.collect_email;
            
            if (!collectName && !collectEmail) {
                return '';
            }
            
            return `
                <div class="quiz-user-info">
                    <h3>Your Information</h3>
                    <div class="user-info-fields">
                        ${collectName ? `
                            <div class="form-field">
                                <label for="quiz-user-name">Name *</label>
                                <input type="text" id="quiz-user-name" name="user_name" placeholder="Enter your name" required>
                            </div>
                        ` : ''}
                        ${collectEmail ? `
                            <div class="form-field">
                                <label for="quiz-user-email">Email *</label>
                                <input type="email" id="quiz-user-email" name="user_email" placeholder="Enter your email" required>
                            </div>
                        ` : ''}
                    </div>
                </div>
            `;
        }

        renderQuestions() {
            return this.quiz.questions.map((question, index) => `
                <div class="quiz-question" data-question-index="${index}">
                    <div class="question-header">
                        <span class="question-number">Question ${index + 1} of ${this.quiz.questions.length}</span>
                        <h3 class="question-text">${this.escapeHtml(question.question)}</h3>
                        ${question.description ? `<p class="question-description">${this.escapeHtml(question.description)}</p>` : ''}
                        ${question.image_url ? `<img src="${question.image_url}" alt="Question image" class="question-image">` : ''}
                    </div>
                    <div class="question-answers">
                        ${this.renderAnswers(question, index)}
                    </div>
                </div>
            `).join('');
        }

        renderCurrentStepQuestions(questions, startIndex) {
            return questions.map((question, index) => {
                const questionIndex = startIndex + index;
                return `
                    <div class="quiz-question" data-question-index="${questionIndex}">
                        <div class="question-header">
                            <span class="question-number">Question ${questionIndex + 1} of ${this.quiz.questions.length}</span>
                            <h3 class="question-text">${this.escapeHtml(question.question)}</h3>
                            ${question.description ? `<p class="question-description">${this.escapeHtml(question.description)}</p>` : ''}
                            ${question.image_url ? `<img src="${question.image_url}" alt="Question image" class="question-image">` : ''}
                        </div>
                        <div class="question-answers">
                            ${this.renderAnswers(question, questionIndex)}
                        </div>
                    </div>
                `;
            }).join('');
        }

        renderStepNavigation() {
            const stepNavigation = this.quiz.settings.step_navigation || 'numbers';
            
            if (stepNavigation === 'progress_bar') {
                const progress = ((this.currentStep + 1) / this.totalSteps) * 100;
                return `
                    <div class="step-navigation progress-bar">
                        <div class="progress-container">
                            <div class="progress-bar-fill" style="width: ${progress}%"></div>
                        </div>
                        <div class="progress-text">Step ${this.currentStep + 1} of ${this.totalSteps}</div>
                    </div>
                `;
            } else if (stepNavigation === 'custom' && this.quiz.settings.custom_step_labels) {
                const labels = this.quiz.settings.custom_step_labels;
                return `
                    <div class="step-navigation custom-labels">
                        <div class="step-indicators">
                            ${Array.from({length: this.totalSteps}, (_, i) => `
                                <div class="step-indicator ${i === this.currentStep ? 'active' : ''} ${i < this.currentStep ? 'completed' : ''}">
                                    <div class="step-circle">${i + 1}</div>
                                    <div class="step-label">${labels[i] || `Step ${i + 1}`}</div>
                                </div>
                            `).join('')}
                        </div>
                    </div>
                `;
            } else {
                // Default numbers
                return `
                    <div class="step-navigation numbers">
                        <div class="step-indicators">
                            ${Array.from({length: this.totalSteps}, (_, i) => `
                                <div class="step-indicator ${i === this.currentStep ? 'active' : ''} ${i < this.currentStep ? 'completed' : ''}">
                                    <div class="step-circle">${i + 1}</div>
                                </div>
                            `).join('')}
                        </div>
                        <div class="step-text">Step ${this.currentStep + 1} of ${this.totalSteps}</div>
                    </div>
                `;
            }
        }

        getStepTitle() {
            const stepNavigation = this.quiz.settings.step_navigation || 'numbers';
            
            if (stepNavigation === 'custom' && this.quiz.settings.custom_step_labels) {
                const labels = this.quiz.settings.custom_step_labels;
                return labels[this.currentStep] || `Step ${this.currentStep + 1}`;
            }
            
            return `Step ${this.currentStep + 1}`;
        }

        saveUserFormData() {
            // Save user information if present
            const userNameField = this.container.find('input[name="user_name"]');
            const userEmailField = this.container.find('input[name="user_email"]');
            
            if (userNameField.length > 0) {
                this.userFormData.user_name = userNameField.val() || '';
            }
            if (userEmailField.length > 0) {
                this.userFormData.user_email = userEmailField.val() || '';
            }
        }

        restoreUserFormData() {
            // Restore user information if available
            if (this.userFormData.user_name !== undefined) {
                const userNameField = this.container.find('input[name="user_name"]');
                if (userNameField.length > 0) {
                    userNameField.val(this.userFormData.user_name);
                }
            }
            if (this.userFormData.user_email !== undefined) {
                const userEmailField = this.container.find('input[name="user_email"]');
                if (userEmailField.length > 0) {
                    userEmailField.val(this.userFormData.user_email);
                }
            }
        }

        renderAnswers(question, questionIndex) {
            return question.answers.map((answer) => {
                const isChecked = this.answers[question.id] === answer.id;
                return `
                    <label class="answer-option">
                        <input type="radio" name="question_${questionIndex}" value="${answer.id}" data-question-id="${question.id}" ${isChecked ? 'checked' : ''}>
                        <span class="answer-content">
                            ${answer.image_url ? `<img src="${answer.image_url}" alt="Answer image" class="answer-image">` : ''}
                            <span class="answer-text">${this.escapeHtml(answer.text)}</span>
                        </span>
                    </label>
                `;
            }).join('');
        }

        attachStepEvents() {
            const self = this;
            
            // Previous button
            this.container.find('.quiz-prev-btn').on('click', function() {
                self.goToPreviousStep();
            });
            
            // Next button
            this.container.find('.quiz-next-btn').on('click', function() {
                self.goToNextStep();
            });
            
            // Submit button
            this.container.find('.quiz-submit-btn').on('click', function() {
                self.submitQuiz();
            });
            
            // Save user form data when inputs change
            this.container.find('input[name="user_name"], input[name="user_email"]').on('input change', function() {
                self.saveUserFormData();
            });
            
            // Answer selection
            this.container.find('input[type="radio"]').on('change', function() {
                self.updateAnswers();
            });
        }

        goToPreviousStep() {
            // Save user form data and current answers before navigation
            this.saveUserFormData();
            this.updateAnswers();
            
            if (this.currentStep > 0) {
                this.currentStep--;
                this.renderStepByStepQuiz();
            }
        }

        goToNextStep() {
            // Save user form data and current answers before navigation
            this.saveUserFormData();
            this.updateAnswers();
            
            // Validate current step questions are answered
            const questionsPerStep = this.quiz.settings.questions_per_step || 1;
            const startIndex = this.currentStep * questionsPerStep;
            const endIndex = Math.min(startIndex + questionsPerStep, this.quiz.questions.length);
            const currentQuestions = this.quiz.questions.slice(startIndex, endIndex);
            
            // Check if all current step questions are answered
            const unansweredQuestions = currentQuestions.filter(question => !this.answers[question.id]);
            
            if (unansweredQuestions.length > 0) {
                showCustomModal('Required Questions', 'Please answer all questions before proceeding to the next step.', 3000, 'error');
                return;
            }
            
            if (this.currentStep < this.totalSteps - 1) {
                this.currentStep++;
                this.renderStepByStepQuiz();
            }
        }

        updateAnswers() {
            // Update answers from current step
            const self = this;
            this.container.find('input[type="radio"]:checked').each(function() {
                const questionId = $(this).data('question-id');
                const answerId = $(this).val();
                if (questionId && answerId) {
                    self.answers[questionId] = answerId;
                }
            });
        }

        attachEvents() {
            const self = this;
            
            this.container.find('.quiz-submit-btn').on('click', function() {
                self.submitQuiz();
            });
            
            // Save user form data when inputs change
            this.container.find('input[name="user_name"], input[name="user_email"]').on('input change', function() {
                self.saveUserFormData();
            });
        }

        submitQuiz() {
            const self = this;
            
            // Save current user form data and answers before submission
            this.saveUserFormData();
            this.updateAnswers();
            
            // Collect user info if needed
            if (this.quiz.settings.collect_name || this.quiz.settings.collect_email) {
                // Use saved form data instead of reading from DOM
                const userName = this.userFormData.user_name || '';
                const userEmail = this.userFormData.user_email || '';
                
                // Validate user info
                if (this.quiz.settings.collect_name && !userName) {
                    showCustomModal('Error', 'Please enter your name.', 3000, 'error');
                    // Try to focus the field if it's visible
                    const nameField = this.container.find('input[name="user_name"]');
                    if (nameField.length > 0) {
                        nameField.focus();
                    }
                    return;
                }
                
                if (this.quiz.settings.collect_email && !userEmail) {
                    showCustomModal('Error', 'Please enter your email.', 3000, 'error');
                    // Try to focus the field if it's visible
                    const emailField = this.container.find('input[name="user_email"]');
                    if (emailField.length > 0) {
                        emailField.focus();
                    }
                    return;
                }
                
                // Validate email format
                if (this.quiz.settings.collect_email && userEmail) {
                    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                    if (!emailRegex.test(userEmail)) {
                        showCustomModal('Error', 'Please enter a valid email address.', 3000, 'error');
                        // Try to focus the field if it's visible
                        const emailField = this.container.find('input[name="user_email"]');
                        if (emailField.length > 0) {
                            emailField.focus();
                        }
                        return;
                    }
                }
                
                this.userData = {
                    user_name: userName,
                    user_email: userEmail
                };
            }

            // Validate all questions answered
            if (Object.keys(this.answers).length < this.quiz.questions.length) {
                showCustomModal('Error', 'Please answer all questions before submitting.', 3000, 'error');
                return;
            }

            // Show processing modal
            showCustomModal('Processing', 'Submitting your quiz...', 30000, 'info');

            // Submit
            $.ajax({
                url: simpleform_ajax.ajax_url,
                type: 'POST',
                data: {
                    action: 'simpleform_submit_quiz',
                    quiz_id: self.quizId,
                    answers: self.answers,
                    user_data: self.userData,
                    nonce: simpleform_ajax.nonce
                },
                success: function(response) {
                    // Close processing modal safely
                    $('.custom-modal-overlay').each(function() {
                        if (document.body.contains(this)) {
                            $(this).remove();
                        }
                    });
                    
                    if (response.success) {
                        // Save to localStorage for tracking
                        const storageKey = `simpleform_quiz_taken_${self.quizId}`;
                        localStorage.setItem(storageKey, 'true');
                        
                        showCustomModal('Success', 'Quiz submitted successfully!', 2000, 'success');
                        setTimeout(() => {
                            self.showResults(response.data);
                        }, 2000);
                    } else {
                        showCustomModal('Error', response.data.message || 'Failed to submit quiz', 3000, 'error');
                    }
                },
                error: function() {
                    // Close processing modal safely
                    $('.custom-modal-overlay').each(function() {
                        if (document.body.contains(this)) {
                            $(this).remove();
                        }
                    });
                    showCustomModal('Error', 'Failed to submit quiz. Please try again.', 3000, 'error');
                }
            });
        }

        showResults(data) {
            const passed = data.passed;
            const score = data.score;
            
            let html = `
                <div class="quiz-results-content ${passed ? 'passed' : 'failed'}">
                    <h3>${passed ? '🎉 Congratulations!' : '😔 Not Quite'}</h3>
                    <p class="score-text">You scored ${score.correct_count} out of ${score.total_questions} (${score.score_percentage}%)</p>
                    <p class="passing-score">Passing score: ${data.passing_score}%</p>
                    
                    ${data.reward ? `
                        <div class="quiz-reward">
                            <h4>Your Reward:</h4>
                            ${data.reward.type === 'coupon' ? `
                                <p class="reward-coupon">Coupon Code: <strong>${data.reward.value}</strong></p>
                            ` : ''}
                            ${data.redirect_url ? `
                                <p><a href="${data.redirect_url}" class="reward-link">Claim Your Reward</a></p>
                            ` : ''}
                        </div>
                    ` : ''}
                    
                    ${data.correct_answers ? this.renderCorrectAnswers(data.correct_answers) : ''}
                </div>
            `;
            
            this.container.find('.quiz-content, .quiz-footer, .quiz-user-info').hide();
            this.container.find('.quiz-results').html(html).show();
        }

        renderCorrectAnswers(results) {
            // Convert results object to array
            const resultsArray = Object.values(results);
            
            return `
                <div class="correct-answers-section">
                    <h4>Review Your Answers:</h4>
                    ${resultsArray.map((result, index) => `
                        <div class="answer-review ${result.is_correct ? 'correct' : 'incorrect'}">
                            <p><strong>Question ${index + 1}:</strong> ${result.is_correct ? '✓ Correct' : '✗ Incorrect'}</p>
                        </div>
                    `).join('')}
                </div>
            `;
        }

        showError(message) {
            this.container.html(`<div class="quiz-error">${this.escapeHtml(message)}</div>`);
        }

        escapeHtml(text) {
            const div = document.createElement('div');
            div.textContent = text;
            return div.innerHTML;
        }
    }

    // Initialize quizzes on page load
    $(document).ready(function() {
        $('.simpleform-quiz-container').each(function() {
            new QuizFrontend(this);
        });
    });

})(jQuery);
